// Dependencies
import React, { useState, Suspense } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';

// Styles
import InputFactoryStyles from './inputFactory.styles';

const Select = React.lazy(() => import('react-select'));

export default function InputFactory({
  type,
  id,
  name,
  label,
  placeholder,
  required,
  value,
  onChange,
  options,
  error,
  disabled,
  accept,
  className,
  clearable,
  instructions,
}) {
  const inputId = id || name;
  const [files, setFiles] = useState([]);
  const [internalEvent, setInternalEvent] = useState([]);
  const [internalActionMeta, setInternalActionMeta] = useState([]);
  const [dropzoneError, setDropzoneError] = useState(null);

  const handleChange = (event, actionMeta) => {
    onChange(event, actionMeta);

    setInternalEvent(event);
    setInternalActionMeta(actionMeta);
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept,
    maxFiles: 4,
    maxSize: 4500000,
    onDrop: (acceptedFiles) => {
      setDropzoneError(null);

      setFiles(
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          }),
        ),
      );

      const event = {
        target: {
          name,
          value: acceptedFiles,
        },
      };

      handleChange(event);
    },
    onDropRejected: (rejectedFiles) => {
      const fileError = rejectedFiles[0]?.errors[0];

      if (fileError) {
        const errorCode = fileError?.code;

        if (errorCode === 'file-too-large') {
          setDropzoneError('One or more files were larger than 4.5MB.');
        }

        if (errorCode === 'too-many-files') {
          setDropzoneError('Too many files were selected.');
        }
      }
    },
  });

  const removePhoto = (fileName) => {
    setFiles(files.filter((item) => item.name !== fileName));

    const overwrittenValues = internalEvent.target.value.filter(
      (file) => file.name !== fileName,
    );

    internalEvent.target.value = overwrittenValues;

    handleChange(internalEvent, internalActionMeta, [fileName]);
  };

  const renderInput = () => {
    let input = '';

    if (
      type === 'text' ||
      type === 'email' ||
      type === 'textarea' ||
      type === 'checkbox' ||
      type === 'hidden'
    ) {
      const Element = type === 'textarea' ? 'textarea' : 'input';

      input = (
        <>
          <Element
            type={type}
            id={inputId}
            name={name}
            placeholder={`${placeholder}${required ? '*' : ''}`}
            required={required}
            value={value || ''}
            onChange={handleChange}
            disabled={disabled}
            className={`${type} ${className}`}
          />

          {type === 'checkbox' && <span className="styled-checkbox" />}
        </>
      );
    } else if (type === 'select') {
      input = (
        <Suspense fallback="">
          <Select
            id={inputId}
            instanceId={inputId}
            name={name}
            required={required}
            options={options}
            onChange={handleChange}
            disabled={disabled}
            placeholder={placeholder || 'Please select an option'}
            className={`react-select-wrapper ${className}`}
            classNamePrefix="react-select"
            isClearable={clearable}
          />
        </Suspense>
      );
    } else if (type === 'file') {
      input = (
        <div className="dropzone-wrapper">
          {/* eslint-disable react/jsx-props-no-spreading */}
          <div
            {...getRootProps({
              className: `dropzone ${files.length ? 'files' : ''}`,
            })}
          >
            <input {...getInputProps()} name={name} key={files} />
            <p>Drag &amp; drop or click to select files.</p>
            <button type="button" className="btn dark">
              Upload
            </button>
          </div>
          {!!files.length && (
            <div className="previews">
              {files.map((file) => (
                <div className="preview-block" key={file.name}>
                  <img
                    src={file.preview}
                    key={file.name}
                    alt="Uploaded file"
                    className="preview-image"
                    title={file.name}
                  />

                  <span className="file-name" title={file.name}>
                    {file.name}
                  </span>

                  <button
                    type="button"
                    className="remove-file"
                    aria-label="Remove file"
                    onClick={() => {
                      removePhoto(file.name);
                    }}
                  >
                    Remove
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 320 512"
                      fill="#535E6B"
                      className="icon"
                    >
                      <path d="M315.3 411.3c-6.253 6.253-16.37 6.253-22.63 0L160 278.6l-132.7 132.7c-6.253 6.253-16.37 6.253-22.63 0c-6.253-6.253-6.253-16.37 0-22.63L137.4 256L4.69 123.3c-6.253-6.253-6.253-16.37 0-22.63c6.253-6.253 16.37-6.253 22.63 0L160 233.4l132.7-132.7c6.253-6.253 16.37-6.253 22.63 0c6.253 6.253 6.253 16.37 0 22.63L182.6 256l132.7 132.7C321.6 394.9 321.6 405.1 315.3 411.3z" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return input;
  };

  return (
    <InputFactoryStyles error={error}>
      <div className="input-group">
        {label ? (
          <label htmlFor={inputId} className={type}>
            {label}
            {required ? '*' : ''} {renderInput()}
          </label>
        ) : (
          renderInput()
        )}

        <div className="instructions">{instructions}</div>

        {error && <div className="error">{error}</div>}
        {dropzoneError && <div className="error">{dropzoneError}</div>}
      </div>
    </InputFactoryStyles>
  );
}

InputFactory.propTypes = {
  type: PropTypes.string.isRequired,
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.number,
    PropTypes.bool,
  ]),
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  error: PropTypes.string,
  disabled: PropTypes.bool,
  accept: PropTypes.shape({}),
  className: PropTypes.string,
  clearable: PropTypes.bool,
  instructions: PropTypes.string,
};

InputFactory.defaultProps = {
  id: '',
  label: '',
  placeholder: '',
  required: false,
  value: '',
  onChange: () => {},
  options: [],
  error: '',
  disabled: false,
  accept: {},
  className: '',
  clearable: false,
  instructions: null,
};
